<template>
  <div>
    <!-- <div class="py-2">
      <h3 class="section-title">Review</h3>
      <p class="section-subtitle">Confirm the details before you submit.</p>
    </div> -->

    <div class="review-list">
      <div class="review-section">
        <div class="review-section-title">Basic Info</div>
        <div class="review-card">
          <div class="review-row review-row--title-bottom"><span>Business Name</span><strong>{{ basicInfo.business_name || '-' }}</strong></div>
          <div class="review-row review-row--title-bottom"><span>Business Type</span><strong>{{ basicInfo.business_type || '-' }}</strong></div>
          <div class="review-row review-row--title-bottom">
            <span>{{ basicInfo.business_type === 'freelancer' ? 'Social Security Number' : 'EIN / Tax ID' }}</span>
            <strong>{{ basicInfo.tax_id || '-' }}</strong>
          </div>
          <div class="review-row review-row--title-bottom"><span>Service Category</span><strong>{{ basicInfo.service_category || '-' }}</strong></div>
        </div>
      </div>

      <div class="review-section">
        <template v-if="hasContactPerson">
          <div class="review-section-title">Contact Person</div>
          <div class="review-card">
            <div class="review-row"><span>First Name</span><strong>{{ resolvedContactPerson.first_name || '-' }}</strong></div>
            <div class="review-row"><span>Last Name</span><strong>{{ resolvedContactPerson.last_name || '-' }}</strong></div>
            <div class="review-row"><span>Email</span><strong>{{ resolvedContactPerson.email || '-' }}</strong></div>
            <div class="review-row"><span>Phone</span><strong>{{ resolvedContactPerson.phone || resolvedContactPerson.phone1 || '-' }}</strong></div>
            <div class="review-row"><span>Alt. Phone</span><strong>{{ resolvedContactPerson.phone_alt || resolvedContactPerson.phone2 || '-' }}</strong></div>
          </div>
        </template>
      </div>

      <div class="review-section">
        <div class="review-section-title">Address</div>
        <div class="review-card">
          <div class="review-row"><span>Address Line 1</span><strong>{{ address.line_1 || '-' }}</strong></div>
          <div class="review-row"><span>Address Line 2</span><strong>{{ address.line_2 || '-' }}</strong></div>
          <div class="review-row"><span>City</span><strong>{{ address.city || '-' }}</strong></div>
          <div class="review-row"><span>State</span><strong>{{ address.state || '-' }}</strong></div>
          <div class="review-row"><span>ZIP</span><strong>{{ address.zip || '-' }}</strong></div>
        </div>
      </div>

      <div class="review-section">
        <div class="review-section-title">Documents</div>
        <div class="review-card">
          <div class="review-row review-row--title-bottom">
            <strong>ID Proof (Front)</strong>
            <div class="review-file">
              <img
                v-if="idFrontPreviewUrl"
                class="review-file-thumb"
                :src="idFrontPreviewUrl"
                :alt="documents.id_front?.name || 'ID Proof (Front)'"
              />
              <span class="review-file-name">{{ documents.id_front?.name || "-" }}</span>
            </div>
          </div>
          <div class="review-row review-row--title-bottom">
            <strong>ID Proof (Back)</strong>
            <div class="review-file">
              <img
                v-if="idBackPreviewUrl"
                class="review-file-thumb"
                :src="idBackPreviewUrl"
                :alt="documents.id_back?.name || 'ID Proof (Back)'"
              />
              <span class="review-file-name">{{ documents.id_back?.name || "-" }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="review-section">
        <div class="review-section-title">Selfie</div>
        <div class="review-card">
          <div class="review-row review-row--title-bottom">
         
            <div class="review-file">
              <img
                v-if="selfiePreviewUrl"
                class="review-file-thumb"
                :src="selfiePreviewUrl"
                :alt="selfie?.name || 'Selfie'"
              />
              <span class="review-file-name">{{ selfie?.name || "-" }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="confirm-card mb-6">
      <v-checkbox
        :model-value="agree_terms"
        color="success"
        hide-details
        @update:modelValue="$emit('update:agree_terms', Boolean($event))"
      >
        <template #label>
          <span>
            I agree to the
            <button
              type="button"
              class="terms-link"
              @click.stop="$emit('click:terms')"
            >
              terms and conditions
            </button>.
          </span>
        </template>
      </v-checkbox>
      <div v-if="errors.agree_terms" class="field-error">{{ errors.agree_terms }}</div>

      <v-checkbox
        :model-value="certify_information"
        color="success"
        label="I certify that the information provided is accurate."
        hide-details
        @update:modelValue="$emit('update:certify_information', Boolean($event))"
      />
      <div v-if="errors.certify_information" class="field-error">
        {{ errors.certify_information }}
      </div>
    </div>

    <!-- Membership Package Section -->
    <div class="review-section" v-if="membership_package || shouldShowPayment">
      <div class="review-section-title">Membership Package</div>
      <div class="review-card">
        <div class="review-row review-row--title-bottom">
          <span>Selected Package</span>
          <div>
            <strong>{{ membership_package || '-' }}</strong>
           <strong v-if="shouldShowPayment">
              ${{ membership_package_data[billing_interval === 'yearly' ? 'price_yearly' : 'price_monthly'] }} / 
              {{ billing_interval === 'yearly' ? 'yearly' : 'monthly' }}
            </strong>

          </div>

        </div>

      </div>
    </div>

    <!-- Payment Section -->
    <div v-if="shouldShowPayment" class="review-section">
      <!-- <div class="review-section-title">Payment</div> -->
      <slot name="payment">
        <PaymentBlock
          ref="paymentBlockRef"
          :publishableKey="resolvedStripePublishableKey"
          :stripeAccount="resolvedStripeAccount"
          :amount="amountDue"
        />
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import PaymentBlock from "@/shared/components/ui/flow/PaymentBlock.vue";

const defaultStripePublishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || "";

const props = withDefaults(
  defineProps<{
    stripe_publishable_key?: string;
    stripe_account?: string;
    membership_package: string;
    membership_package_data: {
      id: string | number;
      label: string;
      highlight: string;
      features: string[];
      price_monthly: string | number;
      price_yearly: string | number;
    } | null;
    billing_interval: "monthly" | "yearly";
    basicInfo: {
      business_name: string;
      business_type: string;
      service_category: string;
      tax_id: string;
    };
    contactPerson?: {
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      phone_alt?: string;
      phone1?: string;
      phone2?: string;
    };
    address: {
      line_1: string;
      line_2: string;
      city: string;
      state: string;
      zip: string;
      lat: string | null;
      lng: string | null;
      country_code: string | null;
      country: string | null;
    };
    documents: {
      id_front: File | null;
      id_back: File | null;
    };
    selfie: File | null;
    agree_terms: boolean;
    certify_information: boolean;
    errors: {
      agree_terms?: string;
      certify_information?: string;
    };
  }>(),
  {
    stripe_publishable_key: "",
    stripe_account: "",
    membership_package: "",
    membership_package_data: null,
    billing_interval: "monthly",
    basicInfo: () => ({
      business_name: "",
      business_type: "",
      service_category: "",
      tax_id: "",
    }),
    contactPerson: () => ({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      phone_alt: "",
      phone1: "",
      phone2: "",
    }),
    address: () => ({
      line_1: "",
      line_2: "",
      city: "",
      state: "",
      zip: "",
      lat: null,
      lng: null,
      country_code: null,
      country: null,
    }),
    documents: () => ({
      id_front: null,
      id_back: null,
    }),
    selfie: null,
    agree_terms: false,
    certify_information: false,
    errors: () => ({}),
  },
);

const resolvedStripePublishableKey = computed(
  () => props.stripe_publishable_key || defaultStripePublishableKey,
);
const resolvedStripeAccount = computed(() => props.stripe_account || "");

const resolvedContactPerson = computed(() => {
  return (
    props.contactPerson || {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      phone_alt: "",
      phone1: "",
      phone2: "",
    }
  );
});

const hasContactPerson = computed(() => {
  const c = resolvedContactPerson.value;
  return Boolean(
    (c.first_name || "").trim() ||
      (c.last_name || "").trim() ||
      (c.email || "").trim() ||
      (c.phone || "").trim() ||
      (c.phone_alt || "").trim() ||
      (c.phone1 || "").trim() ||
      (c.phone2 || "").trim(),
  );
});

const idFrontPreviewUrl = ref<string | null>(null);
const idBackPreviewUrl = ref<string | null>(null);
const selfiePreviewUrl = ref<string | null>(null);

function isImageFile(file: File | null | undefined) {
  const type = file?.type || "";
  return Boolean(type && type.startsWith("image/"));
}

function revokeUrl(target: { value: string | null }) {
  if (target.value) {
    URL.revokeObjectURL(target.value);
    target.value = null;
  }
}

watch(
  () => props.documents?.id_front,
  (file) => {
    revokeUrl(idFrontPreviewUrl);
    if (file && isImageFile(file)) idFrontPreviewUrl.value = URL.createObjectURL(file);
  },
  { immediate: true },
);

watch(
  () => props.documents?.id_back,
  (file) => {
    revokeUrl(idBackPreviewUrl);
    if (file && isImageFile(file)) idBackPreviewUrl.value = URL.createObjectURL(file);
  },
  { immediate: true },
);

watch(
  () => props.selfie,
  (file) => {
    revokeUrl(selfiePreviewUrl);
    if (file && isImageFile(file)) selfiePreviewUrl.value = URL.createObjectURL(file);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  revokeUrl(idFrontPreviewUrl);
  revokeUrl(idBackPreviewUrl);
  revokeUrl(selfiePreviewUrl);
});

const amountDue = computed(() => {
  const data: any = props.membership_package_data;
  if (!data) return null;
  const key = props.billing_interval === "yearly" ? "price_yearly" : "price_monthly";
  const amount = Number(data?.[key]);
  return Number.isFinite(amount) ? amount : null;
});

const shouldShowPayment = computed(() => Number(amountDue.value) > 0);

const paymentBlockRef = ref();

defineEmits(["update:agree_terms", "update:certify_information", "click:terms"]);

defineExpose({
  paymentBlockRef
});
</script>

<style scoped>
.section-title {
  text-transform: capitalize;
}

.section-subtitle {
  margin: 0 0 14px;
  color: #73829a;
  font-size: 14px;
}

.review-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.review-section-title {
  display: inline-block;
  margin-bottom: 6px;
  color: #1f2430;
  font-size: 14px;
  font-weight: 700;
}

.review-card {
  border-radius: 14px;
  background: var(--color-white);
  padding: 2px 14px;
}

.review-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
}

.review-row + .review-row {
  border-top: 1px solid #edf0f5;
}

.review-row span {
  color: var(--color-subtitle-light);
  font-size: 14px;
}

.review-row strong {
  color: #1f2430;
  font-size: 14px;
  text-align: right;
  padding-left: 10px;
  font-weight: 600;
}

.review-row--title-bottom {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.review-row--title-bottom strong {
  text-align: left;
  padding-left: 0;
}

.review-file {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.review-file-thumb {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
}

.review-file-name {
  word-break: break-word;
  font-weight: 400 !important;
}

.confirm-card {
  margin-top: 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid var(--border-color);
  padding: 10px;
}

.confirm-card :deep(.v-label) {
  font-size: 14px;
}

.terms-link {
  color: var(--color-theme);
  font-weight: 700;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
}

.field-error {
  font-size: 12px;
  color: #b42318;
  margin-top: -4px;
  margin-left: 8px;
}
</style>
